import { type ComponentProps, type ReactNode, type Ref, useEffect, useId, useRef } from 'react'
import { cn } from '../lib/cn'

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ref.current = value
  }
}

export function Checkbox({
  children,
  className,
  appearance = 'box',
  id,
  indeterminate = false,
  ref,
  ...props
}: Omit<ComponentProps<'input'>, 'type' | 'children'> & {
  children: ReactNode
  appearance?: 'box' | 'chip'
  indeterminate?: boolean
}) {
  const generatedId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])
  return (
    <label
      htmlFor={id ?? generatedId}
      className={cn(
        'relative inline-flex min-h-11 cursor-pointer items-center gap-2 text-sm has-disabled:cursor-not-allowed has-disabled:opacity-50',
        appearance === 'chip' &&
          'rounded-full border border-border bg-card px-4 has-checked:border-action has-checked:bg-accent has-checked:text-accent-foreground has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-ring',
        className,
      )}
    >
      <input
        {...props}
        ref={(node) => {
          inputRef.current = node
          assignRef(ref, node)
        }}
        id={id ?? generatedId}
        type="checkbox"
        data-slot="checkbox"
        aria-checked={indeterminate ? 'mixed' : props['aria-checked']}
        className={
          appearance === 'chip'
            ? 'sr-only'
            : 'size-4 accent-action focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
        }
      />
      {children}
    </label>
  )
}
